import { ENTITY_MODAL_TYPES } from './entity-modal.constants';
import { useTranslation } from 'react-i18next';
import { FieldLabel, ModalTextarea, CustomSelect, Section, TagList } from './EntityModalComponents';
import type { EntityFormProps } from './entity-modal.types';

const accent = ENTITY_MODAL_TYPES.note.accent;

export function NoteForm({
  isEdit,
  formData,
  onChange,
  domains,
  goals,
  tasks,
  events,
  tags,
  onAddTag,
  onRemoveTag,
}: EntityFormProps) {
  const { t } = useTranslation();
  const domainOptions = domains.map((d) => ({ value: d.id, label: d.name }));
  const goalOptions = [
    { value: '', label: t('common.none_m') },
    ...goals.map((g) => ({ value: g.id, label: g.title })),
  ];
  const taskOptions = [
    { value: '', label: t('common.none_f') },
    ...tasks.map((t) => ({ value: t.id, label: t.title })),
  ];
  const eventOptions = [
    { value: '', label: t('common.none_m') },
    ...events.map((e) => ({ value: e.id, label: e.title })),
  ];

  return (
    <div className="flex flex-col gap-4">
      <input
        type="text"
        required
        placeholder={t('entity.note_title_placeholder')}
        value={formData.title || ''}
        onChange={(e) => onChange('title', e.target.value)}
        className="w-full bg-transparent border-none text-lg font-semibold text-zinc-100 outline-none placeholder:text-zinc-600 tracking-tight"
      />

      <ModalTextarea
        rows={8}
        minHeight="180px"
        placeholder={t('entity.note_content_placeholder')}
        value={formData.content || ''}
        onChange={(val) => onChange('content', val)}
      />

      <Section title={t('common.details')} defaultOpen accent={accent}>
        <div>
          <FieldLabel>{t('common.domain')}</FieldLabel>
          <CustomSelect
            options={domainOptions}
            value={formData.domainId || ''}
            onChange={(val) => onChange('domainId', val)}
          />
        </div>
      </Section>

      <Section title={t('common.attachments')} defaultOpen={isEdit} accent={accent}>
        <div className="flex flex-col gap-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            <div>
              <FieldLabel>{t('entity.note_linked_task')}</FieldLabel>
              <CustomSelect
                options={taskOptions}
                value={formData.taskId || ''}
                onChange={(val) => onChange('taskId', val || null)}
              />
            </div>
            <div>
              <FieldLabel>{t('entity.note_linked_goal')}</FieldLabel>
              <CustomSelect
                options={goalOptions}
                value={formData.goalId || ''}
                onChange={(val) => onChange('goalId', val || null)}
              />
            </div>
          </div>
          <div>
            <FieldLabel>{t('entity.note_linked_event')}</FieldLabel>
            <CustomSelect
              options={eventOptions}
              value={formData.eventId || ''}
              onChange={(val) => onChange('eventId', val || null)}
            />
          </div>
        </div>
      </Section>

      <Section title="Tags" defaultOpen={false} accent={accent}>
        <TagList tags={tags} accent={accent} onAdd={onAddTag} onRemove={onRemoveTag} />
      </Section>
    </div>
  );
}
